import { useContext } from 'react';

import {
  ViewportContext,
  useMatchMediaViewport,
} from './viewport/ViewportContext';
import type { Viewport } from './viewport/ViewportContext';

/**
 * Returns the current viewport as a set of cumulative boolean breakpoint flags
 * (mobile-first): `{ xs, sm, md, lg, xl }`. Each flag is `true` when the
 * viewport width is at or above that breakpoint.
 *
 * ```tsx
 * const viewport = useViewport();
 * return viewport.lg ? <DesktopLayout /> : <MobileLayout />;
 * ```
 *
 * When multiple components in the same tree call `useViewport`, wrap their
 * common ancestor in `ViewportProvider` so they share a single set of
 * `matchMedia` listeners rather than each creating their own:
 *
 * ```tsx
 * <ViewportProvider>
 *   <Header />   // useViewport reads from context
 *   <Sidebar />  // same — no extra listeners
 * </ViewportProvider>
 * ```
 *
 * In tests, pass a static `viewport` prop to avoid mocking `window.matchMedia`:
 *
 * ```tsx
 * import { viewports } from '@farmersdog/corgi-x/test';
 *
 * render(
 *   <ViewportProvider viewport={viewports.large}>
 *     <MyComponent />
 *   </ViewportProvider>
 * );
 * ```
 *
 * @see https://corgi-x.tfd.engineering/system/responsive-design#responsive-react-hook
 */
export function useViewport(): Viewport {
  const contextValue = useContext(ViewportContext);
  const fallbackValue = useMatchMediaViewport(!contextValue);
  return contextValue ?? fallbackValue;
}
